// Copyright 2020 Drivetrain Hub LLC
// GraphQL request helpers for gear data.

import eng from "./eng";

// region QUERY SCHEMA

const TYPENAME = {
  GEAR_DETAIL: 'GearDetail',
  GEAR_CONCEPT: 'GearConcept',
  GEAR_PHASING: 'GearPhasing',
  FRAME: 'Frame',
};

const TYPENAME_GEOM = {
  GEAR_DETAIL: 'GearDetailGeom',
  GEAR_CONCEPT: 'GearConceptGeom',
  GEAR_PHASING: 'GearPhasingGeom',
};

// region GEAR DETAIL

// region GEOM

const CutType = {
  GEN_RACK: 'GENERATING_RACK',
  GEN_PINION: 'GENERATING_PINION',
};

const CutGeneratingRack = `
  dedendumCoefficient
  pressureAngleNormal
  protuberanceAngle
  protuberanceSize
  tipRadiusCoefficient
  tipRadiusMax`;

const CutGeneratingPinion = `
  dedendumCoefficient
  numberOfTeeth
  pressureAngleNormal
  profileShiftCoefficient
  protuberanceSize
  tipRadiusCoefficient
  tipRadiusMax`;

const GearDetailGeom = `
  cutGeneratingRack {
    ${CutGeneratingRack}
  }
  cutGeneratingPinion {
    ${CutGeneratingPinion}
  }
  cutType
  cutWorkflow
  facewidth
  flankRoughnessRa
  helixAngle
  moduleNormal
  numberOfTeeth
  pressureAngleNormal
  rimDiameter
  rootDiameter
  rootRoughnessRa
  tipDiameter
  toothThicknessNormal
  toothThicknessNormalFinish
  workPieceDiameter`;

// endregion

// region MICRO

const MicroType = {
  LINEAR: 'LINEAR',
  PARABOLIC: 'PARABOLIC',
};

const GearMicroLead = `
  crown
  slope
  evalLimitLower
  evalLimitUpper
  bottomRelief
  bottomReliefStart
  bottomReliefType
  topRelief
  topReliefStart
  topReliefType`;

const GearMicroProfile = `
  crown
  slope
  evalLimitLower
  evalLimitUpper
  rootRelief
  rootReliefStart
  rootReliefType
  tipRelief
  tipReliefStart
  tipReliefType`;

const GearMicro = `
  leadLeft {
    ${GearMicroLead}
  }
  leadRight {
    ${GearMicroLead}
  }
  profileLeft {
    ${GearMicroProfile}
  }
  profileRight {
    ${GearMicroProfile}
  }`;

// endregion

// region PROPERTIES

const GearMaterial = `
  agma2001D04 {
    Id
    coreHardnessNumber
  }
  iso633652003 {
    Id
    shotPeen
    surfaceHardnessNumber
  }`;

const GearQualityAGMA = `
  agma13281B14 {
    ${eng.QualityDataAGMA1328_1_B14}
  }
  agma20152B15 {
    ${eng.QualityDataAGMA2015_2_B15}
  }`;

const GearQualityISO = `
  iso132812013 {
    ${eng.QualityDataISO1328_1_2013}
  }
  iso132822020 {
    ${eng.QualityDataISO1328_2_2020}
  }`;

const GearQuality = `
  ${GearQualityAGMA}
  ${GearQualityISO}`;

// endregion

const GearDetail = `
  geom {
    ${GearDetailGeom}
  }
  micro {
    ${GearMicro}
  }
  material {
    ${GearMaterial}
  }
  quality {
    ${GearQuality}
  }`;

// endregion

// region OTHER

const GearConcept = `
  geom {
    facewidth
    helixAngle
    pressureAngleNormal
    theoreticalPitchDiameter
  }`;

const GearPhasing = `
  geom {
    numberOfTeeth
  }`;

const Frame = `
  geom {
    pitchCircleDiameter
    useEqualSpacing
    usePitchCircle
  }`;

// endregion

// endregion

// region MUTATION INPUTS

const INPUT_TYPE = {
  CREATE_GEAR_DETAIL: 'GearDetailInput',
  UPDATE_GEAR_DETAIL: 'UpdateGearDetailInput',
  CREATE_FRAME: 'FrameInput',
  UPDATE_FRAME: 'UpdateFrameInput',
};

// region GEOM

const CutGeneratingRackInput = {
  pressureAngleNormal: null,
  protuberanceAngle: null,
  protuberanceSize: null,
  tipRadiusCoefficient: null,
  tipRadiusMax: null,
  dedendumCoefficient: null,
};

const CutGeneratingPinionInput = {
  dedendumCoefficient: null,
  numberOfTeeth: null,
  pressureAngleNormal: null,
  tipRadiusCoefficient: null,
  tipRadiusMax: null,
  profileShiftCoefficient: null,
  protuberanceSize: null,
};

const GearDetailGeomInput = {
  cutGeneratingRack: {...CutGeneratingRackInput},
  cutGeneratingPinion: {...CutGeneratingPinionInput},
  cutType: null,
  cutWorkflow: null,
  facewidth: null,
  flankRoughnessRa: null,
  helixAngle: null,
  moduleNormal: null,
  numberOfTeeth: null,
  pressureAngleNormal: null,
  rimDiameter: null,
  rootDiameter: null,
  rootRoughnessRa: null,
  tipDiameter: null,
  toothThicknessNormal: null,
  toothThicknessNormalFinish: null,
  workPieceDiameter: null,
};

// endregion

// region PROPERTIES

const MaterialAGMA2001_D04Input = {
  Id: null,
  coreHardnessNumber: null,
};

const MaterialISO6336_5_2003Input = {
  Id: null,
  shotPeen: null,
  surfaceHardnessNumber: null,
};

const GearMaterialInput = {
  agma2001D04: {...MaterialAGMA2001_D04Input},
  iso633652003: {...MaterialISO6336_5_2003Input}
};

const GearQualityInput = {
  agma13281B14: null,
  agma20152B15: null,
  iso132812013: null,
  iso132822020: null,
};

// endregion

const GearDetailInput = {
  name: null,
  number: null,
  description: null,
  lock: null,
  shared: [],
  geom: {...GearDetailGeomInput},
  material: {...GearMaterialInput},
  quality: {...GearQualityInput},
};

// endregion

export default {
  // QUERY SCHEMA
  TYPENAME: TYPENAME,
  TYPENAME_GEOM: TYPENAME_GEOM,
  CutType: CutType,
  GearDetailGeom: GearDetailGeom,
  MicroType: MicroType,
  GearMicro: GearMicro,
  GearMaterial: GearMaterial,
  GearQualityAGMA: GearQualityAGMA,
  GearQualityISO: GearQualityISO,
  GearQuality: GearQuality,
  GearDetail: GearDetail,
  GearConcept: GearConcept,
  GearPhasing: GearPhasing,
  Frame: Frame,
  // MUTATION INPUTS
  INPUT_TYPE: INPUT_TYPE,
  GearDetailGeomInput: GearDetailGeomInput,
  GearDetailInput: GearDetailInput,
}
